/* One theme-scoped consent UI. Shopify is the consent source of truth.
   Never read/write Shopify cookies directly; never record an implicit choice. */
(() => {
  const init = () => {
    const root = document.getElementById('kz-consent');
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const keys = ['analytics', 'marketing', 'preferences'];
    const choices = root.querySelector('#kz-consent-choices');
    const status = root.querySelector('[data-consent-status]');
    const customize = root.querySelector('[data-consent-customize]');
    const save = root.querySelector('[data-consent-save]');
    const buttons = [...root.querySelectorAll('[data-consent-accept],[data-consent-reject],[data-consent-save]')];
    let api, opener, loading, writing = false;
    const showError = () => {
      status.textContent = root.dataset.error;
      status.hidden = false;
      root.dataset.apiState = 'error';
    };
    const readState = () => {
      const state = api.currentVisitorConsent();
      for (const key of keys) {
        root.dataset[key] = state[key] || 'unset';
        root.querySelector('[name="' + key + '"]').checked = state[key] === 'yes';
      }
      root.dataset.analyticsAllowed = String(api.analyticsProcessingAllowed());
      root.dataset.marketingAllowed = String(api.marketingAllowed());
      root.dataset.preferencesAllowed = String(api.preferencesProcessingAllowed());
      root.dataset.apiState = 'ready';
      return state;
    };
    const detailed = () => {
      choices.hidden = false;
      save.hidden = false;
      customize.hidden = true;
      customize.setAttribute('aria-expanded', 'true');
    };
    const show = (details, focus) => {
      root.hidden = false;
      status.hidden = true;
      if (api) readState();
      if (details) detailed();
      if (focus) root.querySelector('h2').focus({preventScroll:true});
    };
    const hide = () => { root.hidden = true; if (opener) opener.focus({preventScroll:true}); };
    const load = () => {
      if (api) return Promise.resolve(api);
      if (loading) return loading;
      loading = new Promise((resolve, reject) => {
        let attempts = 0;
        const begin = () => {
          if (window.Shopify?.customerPrivacy?.currentVisitorConsent) {
            api = window.Shopify.customerPrivacy;
            resolve(api);
          } else if (window.Shopify?.loadFeatures) {
            window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
              if (error || !window.Shopify.customerPrivacy?.currentVisitorConsent) reject(error || new Error('privacy API unavailable'));
              else { api = window.Shopify.customerPrivacy; resolve(api); }
            });
          } else if (++attempts < 40) setTimeout(begin, 250);
          else reject(new Error('Shopify unavailable'));
        };
        begin();
      }).finally(() => { loading = null; });
      return loading;
    };
    const record = async values => {
      if (writing) return;
      writing = true;
      buttons.forEach(button => { button.disabled = true; });
      status.hidden = true;
      try {
        await load();
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('consent timeout')), 10000);
          api.setTrackingConsent(values, result => {
            clearTimeout(timeout);
            if (result?.error) reject(new Error('consent rejected')); else resolve();
          });
        });
        const actual = readState();
        if (!keys.every(key => actual[key] === (values[key] ? 'yes' : 'no'))) throw new Error('consent not persisted');
        hide();
      } catch (_) { showError(); }
      finally { writing = false; buttons.forEach(button => { button.disabled = false; }); }
    };
    root.querySelector('[data-consent-accept]').addEventListener('click', () => record({analytics:true,marketing:true,preferences:true}));
    root.querySelector('[data-consent-reject]').addEventListener('click', () => record({analytics:false,marketing:false,preferences:false}));
    save.addEventListener('click', () => record(Object.fromEntries(keys.map(key => [key, root.querySelector('[name="' + key + '"]').checked]))));
    customize.addEventListener('click', detailed);
    root.querySelector('[data-consent-close]').addEventListener('click', hide);
    root.addEventListener('keydown', event => { if (event.key === 'Escape') { event.stopPropagation(); hide(); } });
    document.addEventListener('click', async event => {
      const button = event.target.closest('[data-kz-privacy-preferences]');
      if (!button) return;
      event.preventDefault();
      opener = button;
      show(true, true);
      try { await load(); readState(); } catch (_) { showError(); }
    });
    document.addEventListener('visitorConsentCollected', () => { if (api) readState(); });
    load().then(() => {
      const state = readState();
      document.documentElement.classList.add('kz-consent-ready');
      if (keys.some(key => !state[key])) show(false, false);
    }).catch(() => { show(false, false); showError(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
})();
